import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lightning,
  MapPinLine,
  Lock,
  Truck,
  Star,
  ArrowRight,
  Phone,
  Quotes,
  Wrench,
  CheckCircle,
  CaretDown,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { getProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/5333b23f-7d24-4df5-8414-8dbb7886c0a2/images/f87f8370138306988e047e8edcee8be4fdab2441cce7399d971df02b7816aeaa.png";
const CAR_BG = "https://static.prod-images.emergentagent.com/jobs/5333b23f-7d24-4df5-8414-8dbb7886c0a2/images/89bd0aefdb90e6d3bf5c1447373a705940100db3a563b73018cd0a1c2c74e4cf.png";
const BIKE_BG = "https://static.prod-images.emergentagent.com/jobs/5333b23f-7d24-4df5-8414-8dbb7886c0a2/images/c436390ec96694653fd8170c2ab1d4d4c4d1f786f7f918153051623752e05c46.png";

const stats = [
  { value: "8,400+", label: "Locks Sold" },
  { value: "14", label: "Regions Served" },
  { value: "113dB", label: "Loudest Alarm" },
  { value: "24/7", label: "Hotline" },
];

const features = [
  {
    icon: ShieldCheck,
    title: "Hardened Steel",
    body: "Boron and chromoly cores tested against bolt-cutters, hacksaws, and impact attacks.",
  },
  {
    icon: Lightning,
    title: "120dB Alarms",
    body: "Motion-triggered sirens loud enough to clear any street in seconds.",
  },
  {
    icon: MapPinLine,
    title: "GPS Recovery",
    body: "Live tracking with geofence alerts on MTN, Vodafone, and AirtelTigo networks.",
  },
  {
    icon: Truck,
    title: "Nationwide Delivery",
    body: "Same-day to Accra & Tema. 24–72hr to Kumasi, Takoradi, Tamale, and beyond.",
  },
];

const testimonials = [
  {
    name: "Kwame A.",
    role: "Okada Rider, Madina",
    body: "The Tundra X9 saved my Boxer last month. The alarm went off and the thief ran. Best 480 cedis I ever spent.",
  },
  {
    name: "Akosua M.",
    role: "Uber Driver, East Legon",
    body: "Installed the Centurion alarm and never looked back. My car has been parked outside for months, no problems.",
  },
  {
    name: "Daniel O.",
    role: "Logistics Manager, Tema",
    body: "We run 22 motorcycles on TrackR GPS. Geofencing helped recover 3 bikes already. Game changer.",
  },
];

const faqs = [
  {
    q: "Do your locks work with all motorcycles and cars?",
    a: "Yes. Our disc and chain locks fit every common motorcycle in Ghana — Boxer, Royal, Apsonic, Haojue, and more. Car alarms install on any 12V vehicle with standard wiring.",
  },
  {
    q: "How does installation work?",
    a: "Disc locks and chains are plug-and-play. For car alarms and GPS trackers, our certified installers in Accra, Kumasi, and Takoradi will book you a 45-minute appointment.",
  },
  {
    q: "What about mobile money payments?",
    a: "All Mobile Money networks supported via Paystack — MTN MoMo, Vodafone Cash, and AirtelTigo. Cards (Visa, Mastercard) are also accepted.",
  },
  {
    q: "Do you offer a warranty?",
    a: "Every product carries at least a 12-month warranty. Premium products (Centurion alarm, TrackR GPS) come with 24 months. We service in-house.",
  },
];

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    getProducts()
      .then((data) => setProducts(data.slice(0, 6)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page" className="bg-[#0a0a0a] text-white">
      {/* HERO */}
      <section className="relative overflow-hidden grain min-h-[92vh] flex items-end">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black" />
          <div className="absolute inset-0 diag-stripes" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-32 w-full">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-8 lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 border border-white/20 px-3 py-1.5 mb-8 font-mono-accent text-[11px] tracking-[0.25em] uppercase"
              >
                <span className="w-2 h-2 bg-[#FFC107] animate-pulse" />
                Built for the Ghana market
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-display font-black uppercase tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.92]"
              >
                Lock it.
                <br />
                <span className="text-[#FFC107]">Alarm it.</span>
                <br />
                Keep it.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-8 text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed"
              >
                Industrial-grade locks, alarms, and GPS trackers engineered for okadas, taxis, trotros, and private cars across Accra, Kumasi, Takoradi, Tamale and beyond.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mt-10 flex flex-wrap gap-4"
              >
                <Link
                  to="/products"
                  data-testid="hero-cta-shop"
                  className="h-14 px-8 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black uppercase tracking-wider text-sm flex items-center gap-2 transition-colors"
                >
                  Shop the catalog <ArrowRight size={18} weight="bold" />
                </Link>
                <a
                  href="#why"
                  data-testid="hero-cta-learn"
                  className="h-14 px-8 border border-white/30 hover:border-[#FFC107] hover:text-[#FFC107] text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-colors"
                >
                  Why ObraSafe <CaretDown size={16} weight="bold" />
                </a>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 backdrop-blur-md bg-black/40">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={s.label} className="flex flex-col" data-testid={`hero-stat-${i}`}>
                <span className="font-display text-3xl md:text-4xl font-black text-[#FFC107]">{s.value}</span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mt-1">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/10 bg-[#FFC107] text-black overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-4 font-display font-black uppercase tracking-wider text-sm md:text-base">
          {Array.from({ length: 2 }).map((_, j) => (
            <div key={j} className="flex items-center gap-10 px-5 shrink-0">
              <span>⬢ MTN MoMo accepted</span>
              <span>⬢ Vodafone Cash</span>
              <span>⬢ AirtelTigo Money</span>
              <span>⬢ Visa & Mastercard</span>
              <span>⬢ Nationwide delivery</span>
              <span>⬢ 12-month warranty</span>
              <span>⬢ Same-day Accra & Tema</span>
              <span>⬢ MTN MoMo accepted</span>
              <span>⬢ Vodafone Cash</span>
              <span>⬢ AirtelTigo Money</span>
              <span>⬢ Visa & Mastercard</span>
              <span>⬢ Nationwide delivery</span>
            </div>
          ))}
        </div>
      </section>

      {/* WHY OBRASAFE */}
      <section id="why" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FFC107] mb-4">// Why ObraSafe</p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight leading-[0.95]">
              Built tough.<br />Tested in Ghana.
            </h2>
          </div>
          <div className="md:col-span-7 text-zinc-300 text-lg leading-relaxed">
            Theft costs Ghana&apos;s riders and drivers millions every year. We obsess over materials, lock cylinders, and battery life so you don&apos;t have to. Every ObraSafe product is field-tested on harmattan dust, rainy-season floods, and the everyday chaos of Accra traffic.
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`feature-${i}`}
              className="bg-[#141414] border border-white/10 p-6 hover:border-[#FFC107] transition-colors"
            >
              <f.icon size={32} weight="duotone" className="text-[#FFC107] mb-5" />
              <h3 className="font-display text-xl font-bold mb-2">{f.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SPLIT CATEGORIES */}
      <section className="border-y border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <Link
            to="/products?cat=motorcycle"
            data-testid="cat-card-motorcycle"
            className="group relative h-[420px] overflow-hidden"
          >
            <img src={BIKE_BG} alt="Motorcycle locks" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="absolute inset-0 p-10 flex flex-col justify-end">
              <p className="text-xs uppercase tracking-[0.25em] text-[#FFC107] mb-3">For okada riders</p>
              <h3 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight mb-3">Motorcycle Vault</h3>
              <p className="text-zinc-300 mb-6 max-w-md">Disc locks, chain locks, grip locks, alarms. Pocket to permanent — protection for every parking situation.</p>
              <span className="text-[#FFC107] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 group-hover:gap-4 transition-all">
                Browse motorcycle <ArrowRight size={16} weight="bold" />
              </span>
            </div>
          </Link>
          <Link
            to="/products?cat=car"
            data-testid="cat-card-car"
            className="group relative h-[420px] overflow-hidden"
          >
            <img src={CAR_BG} alt="Car security" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="absolute inset-0 p-10 flex flex-col justify-end">
              <p className="text-xs uppercase tracking-[0.25em] text-[#FFC107] mb-3">For drivers</p>
              <h3 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight mb-3">Car Fortress</h3>
              <p className="text-zinc-300 mb-6 max-w-md">Two-way alarms, immobilisers, steering claws, GPS trackers. Layered defence for taxis, trotros, and personal cars.</p>
              <span className="text-[#FFC107] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 group-hover:gap-4 transition-all">
                Browse car <ArrowRight size={16} weight="bold" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FFC107] mb-3">// Catalog</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight">
              Featured Hardware
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="featured-view-all"
            className="h-12 px-6 border border-white/20 hover:border-[#FFC107] hover:text-[#FFC107] flex items-center gap-2 text-sm font-bold uppercase tracking-wider"
          >
            View entire catalog <ArrowRight size={16} weight="bold" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-[#141414] border border-white/10 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#141414] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-5">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FFC107] mb-4">// Process</p>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight leading-[0.95]">
                Three steps to a locked-down ride.
              </h2>
            </div>
            <div className="md:col-span-7 text-zinc-400 text-lg leading-relaxed">
              From browsing the catalog to a fitted alarm in your dashboard, we keep the process direct. No middlemen, no overpricing, no surprises.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { icon: Lock, n: "01", t: "Pick your protection", b: "Filter by vehicle. Pick disc locks, alarms, immobilisers, or GPS trackers." },
              { icon: Wrench, n: "02", t: "Pay & schedule", b: "Pay with MoMo or card. Plug-and-play items ship same-day; alarms and GPS get installed in 45 min." },
              { icon: ShieldCheck, n: "03", t: "Ride locked", b: "Walk away knowing your ride is protected by Ghana's toughest hardware." },
            ].map((s, i) => (
              <div key={s.t} className="border border-white/10 p-8 bg-[#0a0a0a]" data-testid={`how-step-${i}`}>
                <div className="flex items-center justify-between mb-6">
                  <s.icon size={32} weight="duotone" className="text-[#FFC107]" />
                  <span className="font-mono-accent text-zinc-600 text-sm">{s.n}</span>
                </div>
                <h3 className="font-display text-2xl font-bold mb-3">{s.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="mb-16">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FFC107] mb-4">// Voices</p>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight leading-[0.95] max-w-3xl">
            Trusted by riders & drivers nationwide.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {testimonials.map((t, i) => (
            <div key={t.name} className="border border-white/10 p-8 bg-[#141414]" data-testid={`testimonial-${i}`}>
              <Quotes size={32} weight="fill" className="text-[#FFC107] mb-4" />
              <p className="text-zinc-200 leading-relaxed mb-6">&ldquo;{t.body}&rdquo;</p>
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, k) => (
                  <Star key={k} size={14} weight="fill" className="text-[#FFC107]" />
                ))}
              </div>
              <div className="font-display font-bold">{t.name}</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-white/10 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FFC107] mb-4">// FAQ</p>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight mb-12">
            Quick answers.
          </h2>
          <div className="border-t border-white/10">
            {faqs.map((f, i) => (
              <div key={f.q} className="border-b border-white/10">
                <button
                  data-testid={`faq-toggle-${i}`}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full py-6 flex items-center justify-between gap-6 text-left"
                >
                  <span className="font-display text-xl md:text-2xl font-bold">{f.q}</span>
                  <CaretDown
                    size={20}
                    weight="bold"
                    className={`text-[#FFC107] transition-transform shrink-0 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="pb-6 text-zinc-300 leading-relaxed max-w-3xl" data-testid={`faq-content-${i}`}>
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 diag-stripes opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h3 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tight leading-[0.95]">
              Stop wishing.<br />Start <span className="text-[#FFC107]">locking</span>.
            </h3>
            <p className="mt-6 text-lg text-zinc-300 max-w-2xl">
              Browse the full catalog or chat with our team on WhatsApp for a personal recommendation.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-4">
            <Link
              to="/products"
              data-testid="footer-cta-shop"
              className="h-14 px-8 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black uppercase tracking-wider text-sm flex items-center justify-center gap-2"
            >
              Shop now <ArrowRight size={18} weight="bold" />
            </Link>
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="h-14 px-8 border border-white/30 hover:border-[#FFC107] hover:text-[#FFC107] text-white font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2"
            >
              Talk to an expert <Phone size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
